import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowDownRight, ArrowUpRight, Loader2, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { MONTH_SHORT, monthName, monthShort, ZONES, ZONE_COLORS, fmt1, useSpotMonths, type SpotMonth, type Zone } from "@/lib/spotHistory";

export const Route = createFileRoute("/admin/trender")({
  head: () => ({
    meta: [
      { title: "Trender – Admin – Elmarknad.se" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Trender,
});

const tooltipStyle = { background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 };
const pct = (a?: number, b?: number) => (a != null && b ? ((a - b) / b) * 100 : null);

function Change({ value }: { value: number | null }) {
  if (value == null) return <span className="text-sm text-muted-foreground">–</span>;
  const Icon = Math.abs(value) < 0.5 ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn("inline-flex items-center gap-1 text-sm font-semibold", value > 0.5 ? "text-destructive" : value < -0.5 ? "text-primary" : "text-muted-foreground")}>
      <Icon className="h-4 w-4" aria-hidden />
      {value > 0 ? "+" : ""}{fmt1(value)} %
      <span className="sr-only">{value > 0 ? "högre" : "lägre"}</span>
    </span>
  );
}

function Trender() {
  const { data, isLoading, error } = useSpotMonths();
  const [zone, setZone] = useState<Zone>("SE3");
  const all: SpotMonth[] = data ?? [];

  const latest = all[all.length - 1];
  const prev = all[all.length - 2];
  const lastYear = latest ? all.find((m) => m.year === latest.year - 1 && m.month === latest.month) : undefined;
  const lastComplete = [...all].reverse().find((m) => m.complete);

  const insights = useMemo(() => {
    const last12 = all.filter((m) => m.complete).slice(-12);
    const prev12 = all.filter((m) => m.complete).slice(-24, -12);
    if (!last12.length) return null;
    const avg = (xs: SpotMonth[]) => xs.reduce((s, m) => s + m[zone], 0) / (xs.length || 1);
    const max = last12.reduce((a, b) => (b[zone] > a[zone] ? b : a));
    const min = last12.reduce((a, b) => (b[zone] < a[zone] ? b : a));
    const spread = last12.map((m) => m.SE4 - m.SE1);
    return {
      avg12: avg(last12),
      change12: pct(avg(last12), prev12.length === 12 ? avg(prev12) : undefined),
      max, min,
      spread: spread.reduce((a, b) => a + b, 0) / spread.length,
    };
  }, [all, zone]);

  const thisYear = latest?.year;
  const yoyChart = MONTH_SHORT.map((label, i) => ({
    label,
    [String(thisYear)]: all.find((m) => m.year === thisYear && m.month === i + 1)?.[zone],
    [String((thisYear ?? 0) - 1)]: all.find((m) => m.year === (thisYear ?? 0) - 1 && m.month === i + 1)?.[zone],
  }));
  const last24 = all.slice(-24).map((m) => ({ label: `${monthShort(m.month)} ${String(m.year).slice(2)}`, SE1: m.SE1, SE2: m.SE2, SE3: m.SE3, SE4: m.SE4 }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Trender</h1>
          <p className="text-sm text-muted-foreground">Hur spotpriset utvecklas – månad för månad och år mot år (öre/kWh exkl. moms).</p>
        </div>
        <div className="flex gap-1 rounded-full border border-border bg-card p-1" role="group" aria-label="Elområde">
          {ZONES.map((z) => (
            <button key={z} type="button" aria-pressed={zone === z} onClick={() => setZone(z)}
              className={cn("rounded-full px-4 py-1.5 text-xs font-semibold transition-colors", zone === z ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
              {z}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Laddar spotpriser…</div>}
      {error && <p className="text-sm text-destructive">Kunde inte hämta spotpriser.</p>}

      {latest && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="border-0 shadow-sm"><CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Snittpris {monthName(latest.month).toLowerCase()} {latest.year}{!latest.complete && " (hittills)"} · {zone}</p>
              <p className="mt-1 text-3xl font-bold tabular-nums">{fmt1(latest[zone])} öre/kWh</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Mot föregående månad ({monthName((prev?.month ?? 1)).toLowerCase()})</p>
              <p className="mt-1 text-xl font-bold tabular-nums">{fmt1(prev?.[zone])} öre</p>
              <Change value={pct(latest[zone], prev?.[zone])} />
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Mot samma månad föregående år</p>
              <p className="mt-1 text-xl font-bold tabular-nums">{lastYear ? `${fmt1(lastYear[zone])} öre` : "–"}</p>
              <Change value={pct(latest[zone], lastYear?.[zone])} />
            </CardContent></Card>
          </div>

          {insights && (
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle>Insikter senaste 12 hela månaderna · {zone}</CardTitle></CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div><p className="text-sm text-muted-foreground">Snitt 12 mån</p><p className="text-xl font-bold tabular-nums">{fmt1(insights.avg12)} öre</p><Change value={insights.change12} /><p className="text-xs text-muted-foreground">mot 12 mån innan</p></div>
                <div><p className="text-sm text-muted-foreground">Dyraste månad</p><p className="text-xl font-bold tabular-nums">{fmt1(insights.max[zone])} öre</p><p className="text-xs text-muted-foreground">{monthName(insights.max.month)} {insights.max.year}</p></div>
                <div><p className="text-sm text-muted-foreground">Billigaste månad</p><p className="text-xl font-bold tabular-nums">{fmt1(insights.min[zone])} öre</p><p className="text-xs text-muted-foreground">{monthName(insights.min.month)} {insights.min.year}</p></div>
                <div><p className="text-sm text-muted-foreground">Snittskillnad SE4 – SE1</p><p className="text-xl font-bold tabular-nums">{fmt1(insights.spread)} öre</p><p className="text-xs text-muted-foreground">Senast hel månad: {lastComplete ? `${monthName(lastComplete.month).toLowerCase()} ${lastComplete.year}` : "–"}</p></div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle>{thisYear} mot {(thisYear ?? 0) - 1} · {zone}</CardTitle></CardHeader>
              <CardContent className="h-[320px] pl-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yoyChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} unit=" öre" width={60} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${fmt1(v)} öre/kWh`} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey={String(thisYear)} stroke="var(--scb-1)" strokeWidth={2.5} dot={false} connectNulls={false} />
                    <Line type="monotone" dataKey={String((thisYear ?? 0) - 1)} stroke="var(--scb-7)" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle>Senaste 24 månaderna · alla elområden</CardTitle></CardHeader>
              <CardContent className="h-[320px] pl-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={last24}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} interval={2} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} unit=" öre" width={60} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${fmt1(v)} öre/kWh`} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    {ZONES.map((z) => (
                      <Line key={z} type="monotone" dataKey={z} stroke={ZONE_COLORS[z]} strokeWidth={z === zone ? 3 : 1.5} strokeOpacity={z === zone ? 1 : 0.5} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
